import {
  Box,
  Button,
  Divider,
  lighten,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePrices } from "src/context/PricesProvider";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useCallback, useMemo } from "react";
import { ProductPrice } from "src/api/types";
import { Check } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import ReviewsCarousel from "../../../../../ReviewsCarousel";
import { usePatient } from "src/context/PatientProvider";
import HSAFSADisclaimer from "src/pages/Onboarding/Payment/components/HSAFSADisclaimer";
import WhiteCard from "src/shared/components/WhiteCard";
import { medicationInfoMap } from "src/pages/Onboarding/Payment/utils/mapMedicationInfoToProduct";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import PaymentProcessor from "src/pages/Onboarding/Payment/components/PaymentProcessor";
import CouponView from "src/pages/Onboarding/Payment/components/CouponView";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import ProductsDisplay from "src/components/ProductsDisplay";

const informationArray = [
  "payment-information.list.2",
  "payment-information.list.3",
];

const WeightLossDisplay = () => {
  const isMobile = useIsMobile();
  const weightLossProducts = usePrices();
  const [selectedProduct, setSelectedProduct] = useSelectedProduct();
  const page = "payment-information";
  const { t } = useTranslation();
  const billingModel = useProduct((product) => product.billing_model);
  const productSlug = useProduct((p) => p.slug);
  const category = useProduct((p) => p.category.name);
  const isGlp1 = useProduct((p) => p.is_glp1);
  const language = usePatient((patient) => patient.profile.language);
  const { data = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const handleProductChange = useCallback(
    (product: ProductPrice) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );

  const isQuarterly = useMemo(
    () => selectedProduct?.number_of_months === 3,
    [selectedProduct?.number_of_months],
  );

  const medicationInfo = useMemo(
    () => medicationInfoMap[productSlug],
    [productSlug],
  );

  const disclaimerPath = useMemo(() => {
    if (!isGlp1) return null;

    const duration = isQuarterly ? "3months" : "4weeks";
    return `${page}.${productSlug}.disclaimer.${duration}`;
  }, [isGlp1, isQuarterly, productSlug]);

  const saving = useMemo(() => {
    if (selectedProduct == null) {
      return 0;
    }

    const competitorPrice = selectedProduct.competitor_price ?? 0;

    if (competitorPrice <= selectedProduct.amount) {
      return 0;
    }

    return competitorPrice - selectedProduct.amount;
  }, [selectedProduct]);

  const coupons = useMemo(() => {
    const defaultCoupon = { name: t(`${page}.payNowDiscount`), amount: saving };
    return [defaultCoupon].concat(data).filter((c) => c.amount > 0);
  }, [data, saving, t]);

  const amount = useMemo(() => {
    if (selectedProduct == null) return 0;
    const discount = data.reduce((acc, c) => (acc += c.amount), 0);

    return Math.max(
      selectedProduct.amount_limit ?? 0,
      selectedProduct.amount - discount,
    );
  }, [data, selectedProduct]);

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    return amount.toLocaleString();
  }, [amount, selectedProduct]);

  const subHeader = useMemo(() => {
    const string = t(`${page}.subheader`);

    const phraseToStyle =
      language === "en" ? "you’ll achieve" : "puedes lograr";
    const parts = string.split(new RegExp(`(${phraseToStyle})`, "i"));

    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === phraseToStyle.toLowerCase() ? (
            <Typography
              key={index}
              component="span"
              color={theme.palette.primary.main}
              sx={{ textDecoration: "underline" }}
              variant={isMobile ? "body1" : "h6"}
              fontWeight={600}
            >
              {part}
            </Typography>
          ) : (
            part
          ),
        )}
      </>
    );
  }, [isMobile, language, t]);

  return (
    <>
      <Stack sx={{ position: "relative" }}>
        <HSAFSADisclaimer category={category} />
        <WhiteCard
          onClick={() => handleFocusIntoView("payment-input")}
          sx={{
            mt: "-15px", // pulls it up by the same radius
            borderRadius: "0 0 10px 10px",
            px: 2,
            paddingBottom: isMobile ? "20px" : "0px",
            zIndex: 1,
            cursor: "pointer",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ position: "relative", bottom: isMobile ? 0 : 8 }}
          >
            <Stack gap={1}>
              <Typography variant={isMobile ? "body1" : "h5"} fontWeight={600}>
                {t(medicationInfo.header)}
              </Typography>
              {medicationInfo.text != null ? (
                <Typography variant={isMobile ? "body2" : "h6"}>
                  {t(medicationInfo.text)}
                </Typography>
              ) : null}
              {disclaimerPath ? (
                <Typography
                  color={theme.palette.primary.main}
                  fontStyle="italic"
                  fontWeight={600}
                  variant={isMobile ? "body2" : "h6"}
                >
                  {t(`${disclaimerPath}`)}
                </Typography>
              ) : null}
            </Stack>
            <MedicationIcon
              slug={productSlug}
              name={medicationInfo.name}
              width={isMobile ? 130 : 180}
              height={isMobile ? 130 : 180}
            />
          </Stack>
        </WhiteCard>
      </Stack>
      <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
        <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
          {subHeader}
        </Typography>
        {informationArray.map((i) => (
          <Box key={i} display="flex" gap={1} alignItems="center">
            <Check
              sx={{ color: theme.palette.primary.main }}
              fontSize="small"
            />
            <Typography variant={isMobile ? "body2" : "body1"}>
              {t(i)}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Stack gap={3}>
        <ProductsDisplay
          page={page}
          handleProductChange={handleProductChange}
          products={weightLossProducts}
          selectedProduct={selectedProduct}
        />

        <Stack gap={4}>
          <Stack gap={2}>
            {coupons.map((c) => (
              <CouponView key={c.name} coupon={c} />
            ))}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography fontSize={"18px"}>
                {t(`${page}.freeShipping`)}
              </Typography>
              <Typography fontSize={"18px"}>{t(`${page}.free`)}</Typography>
            </Stack>
            <Divider />
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
              <Typography
                color={theme.palette.primary.main}
                fontStyle="italic"
                fontWeight={600}
                fontSize={"18px"}
              >
                {t(`${page}.${billingModel}.disclaimer`)}
              </Typography>
            </Stack>
            {saving > 0 ? (
              <Button
                variant="text"
                sx={{
                  backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                  boxShadow: " 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 700,
                  borderRadius: "10px",
                  textAlign: "center",
                }}
                onClick={() => handleFocusIntoView("payment-input")}
              >
                {t(`${page}.totalSavings`, {
                  saving,
                })}
              </Button>
            ) : null}
            <ReviewsCarousel onFocus={handleFocusIntoView} />
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>

          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={null}
              dueToday={amount}
            />
          ) : null}
        </Stack>
      </Stack>
    </>
  );
};

export default WeightLossDisplay;
